import { memo, useMemo } from 'react'
import PropTypes from 'prop-types'

const linearScale =
  ([d0, d1], [r0, r1]) =>
  v =>
    ((v - d0) / (d1 - d0)) * (r1 - r0) + r0

const buildPath = points => {
  if (!points.length) return ''
  return 'M' + points.map(p => `${p.x},${p.y}`).join('L')
}

const Heatmap = ({ questionId, chartId, heatmap, width, height, margin, videoDuration, batch, visible }) => {
  const { maskNodes, gradientUrl, maskId } = useMemo(() => {
    // with no duration there is no time axis to lay the contours along
    if (!heatmap || !Object.keys(heatmap).length || !videoDuration) {
      return { maskNodes: [], gradientUrl: '', maskId: '' }
    }
    const { contours = [], levels = [] } = heatmap

    const getOpacity = linearScale([0, levels.length], [0.05, 0.8])
    const getX = linearScale([0, videoDuration], [0, width - margin[1] * 2])
    const getY = linearScale([0, 100], [0, height - margin[0] * 2])

    const nodes = contours.map((item, i) => {
      const seen = new Set()
      const points = item.contour.reduce((arr, p) => {
        const x = getX(p.x)
        const y = getY(100 - p.y)
        const key = `${x}|${y}`
        if (!seen.has(key)) {
          seen.add(key)
          arr.push({ x, y })
        }
        return arr
      }, [])

      return <path key={i} d={buildPath(points)} fill="#ffffff" fillOpacity={getOpacity(levels.indexOf(item.level))} />
    })

    return {
      maskNodes: nodes,
      gradientUrl: `url(#batch-gradient-${questionId}-${batch})`,
      maskId: `emd-heatmap-clip-${questionId}`
    }
  }, [heatmap, videoDuration, width, height, margin, questionId, batch])

  if (!heatmap || !Object.keys(heatmap).length || !visible) return null

  const innerW = width - margin[1] * 2
  const innerH = height - margin[0] * 2

  return (
    <svg
      id={`heatmap-${chartId}`}
      width={width}
      height={height}
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      viewBox={`0 0 ${width} ${height}`}
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        pointerEvents: 'none'
      }}
    >
      <defs>
        <mask id={maskId}>
          <rect width={innerW} height={innerH} />
          <g filter="url(#gaussianBlur)">{maskNodes}</g>
        </mask>
      </defs>
      <g className="heatmap" mask={`url(#${maskId})`} transform={`translate(${margin[1]}, ${margin[0]})`}>
        <rect width={innerW} height={innerH} fill={gradientUrl} fillOpacity={1} />
      </g>
    </svg>
  )
}

Heatmap.propTypes = {
  questionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  chartId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  heatmap: PropTypes.shape({
    contours: PropTypes.array,
    levels: PropTypes.array
  }),
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  margin: PropTypes.arrayOf(PropTypes.number).isRequired,
  videoDuration: PropTypes.number.isRequired,
  batch: PropTypes.number,
  visible: PropTypes.bool
}

Heatmap.defaultProps = {
  batch: 0,
  visible: true
}

Heatmap.displayName = 'Heatmap'

export default memo(Heatmap)
